import { useMemo, useState } from "react";
import { AlertCircle, Download } from "lucide-react";

import CommentsHistory from "../comments-history";
import {
  ATTACHMENT_KIND_ENUM,
  INTERACTION_TYPES,
  SERVER_ORIGIN,
} from "../consts";
// import { Context } from "../context";
import ReviewStatus from "../review-status";
import {
  callApi,
  fileToBase64,
  fmtDate,
  formatDT,
  getCourseLabel,
} from "../utils";
import VersionHistory from "../version-history";
import Filter from "./filter";
import SubmitCell from "./submit-cell";

const FILE_TYPES = {
  report: { value: "report", accept: ".docx", displayValue: "Отчет" },
  code: { value: "code", accept: ".rar,.zip", displayValue: "Код" },
};

export default function GroupDetails({ group, getMyGroups }) {
  const [practicalsFiltered, setPracticalsFiltered] = useState([]);
  const [error, setError] = useState("");
  const [selectedStatObj, setSelectedStatObj] = useState(null);
  const [isCommentsHistoryOpen, setIsCommentsHistoryOpen] = useState(false);
  const [pendingFileDeletion, setPendingFileDeletion] = useState(null);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);

  /* const store = useContext(Context);

  const checkIsPlacementAllowed = (reviewStatusCode) =>
    store?.reviewStatuses?.find((elem) => elem.code === reviewStatusCode)
      ?.studentPlacementAllowed;

  const checkIsOverdue = (reworkEndDate) =>
    (reworkEndDate ?? "") < (store?.today ?? ""); */

  const groupPracticals = useMemo(() => {
    const practicals = group.practicals;
    setPracticalsFiltered(practicals);
    return practicals;
  }, [group]);

  const handleCommentsClick = (practical) => {
    setSelectedStatObj({ practicalNumber: practical.number });
    setIsCommentsHistoryOpen(true);

    if (practical.hasNewComment) {
      callApi({
        method: "POST",
        path: `api/student/practice-groups/${group.id}/practicals/${practical.number}/comments/read`,
        needAuth: true,
      }).then((response) => {
        if (response.error) {
          setError(
            response.message ?? "При прочтении комментариев произошла ошибка",
          );
        } else {
          setError("");
          getMyGroups();
        }
      });
    }
  };

  const handleVersionHistory = (practicalNumber, kind) => {
    setSelectedStatObj({ practicalNumber, kind });
    setIsVersionHistoryOpen(true);
  };

  const uploadFile = async (file, type, practicalNumber) => {
    const accept = FILE_TYPES[type].accept;
    const exts = accept.split(",");
    let isOK = false;
    for (let i = 0; i < exts.length; ++i) {
      if (file.name.toLowerCase().endsWith(exts[i])) {
        isOK = true;
        break;
      }
    }
    if (isOK) {
      setError("");
    } else {
      setError(
        `${FILE_TYPES[type].displayValue} должен быть в формате ${accept.replace(",", "/")}.`,
      );
      return;
    }

    const base64String = await fileToBase64(file);

    const body = JSON.stringify({
      fileName: file.name,
      fileContent: base64String,
    });
    callApi({
      method: "POST",
      path: `api/student/practice-groups/${group.id}/practicals/${practicalNumber}/${type}`,
      body,
      needAuth: true,
    }).then((response) => {
      if (response.error) {
        setError(response.message ?? "При загрузке файла произошла ошибка");
      } else {
        setError("");
        getMyGroups();
      }
    });
  };

  const requestFileDeletion = (practicalNumber, type) => {
    callApi({
      method: "DELETE",
      path: `api/student/practice-groups/${group.id}/practicals/${practicalNumber}/${type}`,
      needAuth: true,
    }).then((response) => {
      if (response.error) {
        setError(response.message ?? "При удалении файла произошла ошибка");
      } else {
        setError("");
        getMyGroups();
      }
    });
  };

  const deleteFile = (practicalNumber, type, deleteWarning) => {
    if (deleteWarning) {
      setPendingFileDeletion({ practicalNumber, type, deleteWarning });
      return;
    }

    requestFileDeletion(practicalNumber, type);
  };

  const confirmFileDeletion = () => {
    const { practicalNumber, type } = pendingFileDeletion;
    setPendingFileDeletion(null);
    requestFileDeletion(practicalNumber, type);
  };

  return (
    <div className="card">
      <div className="content-head-student" style={{ marginBottom: 8 }}>
        <div>
          <h2>{group.group}</h2>
          <p className="muted">
            {getCourseLabel(group.course)} ·{" "}
            {group.interactionType === INTERACTION_TYPES.individual
              ? "Индивидуальный формат"
              : `Групповой формат · Подгруппа ${group.subgroupNumber}`}
          </p>
        </div>
      </div>

      <Filter
        practicals={groupPracticals}
        setPracticalsFiltered={setPracticalsFiltered}
      />

      <table className="data-table">
        <thead>
          <tr>
            <th>Практика</th>
            <th>Вариант</th>
            <th>Методичка</th>
            <th>Срок</th>
            <th>Доступ</th>
            <th>Отчёт (.docx)</th>
            <th>Исходный код (.rar/.zip)</th>
            <th>Статус проверки</th>
            <th>Комментарий преподавателя</th>
          </tr>
        </thead>
        <tbody>
          {practicalsFiltered.map((p) => {
            return (
              <tr key={p.number}>
                <td className="num-cell">{p.number}</td>
                <td className="num-cell">{p.variant}</td>
                <td>
                  {p.materialLink ? (
                    <a
                      className="link"
                      href={`${SERVER_ORIGIN}${p.materialLink}`}
                    >
                      <Download size={12} /> {p.materialFileName}
                    </a>
                  ) : (
                    <span className="muted small">не размещена</span>
                  )}
                </td>
                <td className="small">
                  {fmtDate(p.startDate)} — {fmtDate(p.endDate)}
                </td>
                <td>
                  <span
                    className={`badge${p.access ? " badge-" + p.access.toLowerCase() : ""}`}
                  >
                    {p.accessLabel}
                  </span>
                </td>
                <td>
                  <SubmitCell
                    accept={FILE_TYPES.report.accept}
                    attachmentActions={p.reportActions}
                    status={p.status}
                    link={p.reportLink}
                    fileName={`Отчёт v${p.reportVersion}`}
                    onHistory={() =>
                      handleVersionHistory(
                        p.number,
                        ATTACHMENT_KIND_ENUM.report,
                      )
                    }
                    /* reviewStatus={p.reviewStatus} */
                    onFile={(f) =>
                      uploadFile(f, FILE_TYPES.report.value, p.number)
                    }
                    onDelete={() =>
                      deleteFile(
                        p.number,
                        FILE_TYPES.report.value,
                        p.reportDeleteWarning,
                      )
                    }
                    /* checkIsPlacementAllowed={() =>
                      checkIsPlacementAllowed(p.reviewStatus?.code)
                    }
                    checkIsOverdue={() => checkIsOverdue(p.reworkEndDate)} */
                  />
                </td>
                <td>
                  <SubmitCell
                    accept={FILE_TYPES.code.accept}
                    attachmentActions={p.codeActions}
                    status={p.status}
                    link={p.codeLink}
                    fileName={`Код v${p.codeVersion}`}
                    onHistory={() =>
                      handleVersionHistory(p.number, ATTACHMENT_KIND_ENUM.code)
                    }
                    /* reviewStatus={p.reviewStatus} */
                    onFile={(f) =>
                      uploadFile(f, FILE_TYPES.code.value, p.number)
                    }
                    onDelete={() =>
                      deleteFile(
                        p.number,
                        FILE_TYPES.code.value,
                        p.codeDeleteWarning,
                      )
                    }
                    /* checkIsPlacementAllowed={() =>
                      checkIsPlacementAllowed(p.reviewStatus?.code)
                    }
                    checkIsOverdue={() => checkIsOverdue(p.reworkEndDate)} */
                  />
                </td>
                <td>
                  <ReviewStatus
                    color={p.reviewStatus?.color}
                    name={p.reviewStatus?.name}
                  />
                </td>
                <td>
                  {p.lastCommentAt && (
                    <div className="comment-column">
                      <div>
                        {p.hasNewComment && <span className="tag">Новое</span>}
                        <div>{formatDT(p.lastCommentAt)}</div>
                        <div
                          className="last-comment"
                          title={p.lastCommentPreview}
                        >
                          {p.lastCommentPreview}
                        </div>
                      </div>
                      <button
                        className="btn tiny"
                        onClick={() => handleCommentsClick(p)}
                      >
                        Открыть
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="warning small">
        При наступлении даты завершения Вы не сможете разместить отчет и
        исходный код программы, если не разместили их до этой даты. Доступ будет
        заблокирован. Работа будет считаться <b>невыполненной</b>. Если работа
        размещена в срок, преподаватель может запросить доработку – в этом
        случае Вы сможете добавить новые версии файлов
      </p>

      {error && (
        <div className="error-box-container">
          <div className="error-box no-columns">
            <AlertCircle size={14} /> {error}
          </div>
        </div>
      )}
      {!!group.subgroupMembers?.length && (
        <div className="subgroup-members">
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Перечень студентов подгруппы</th>
                </tr>
              </thead>
              <tbody>
                {group.subgroupMembers.map((m) => (
                  <tr key={m}>
                    <td>{m}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {isCommentsHistoryOpen && (
        <CommentsHistory
          apiPath={`api/student/practice-groups/${group.id}/practicals/${selectedStatObj?.practicalNumber}/comments`}
          onCancel={() => setIsCommentsHistoryOpen(false)}
        />
      )}
      {isVersionHistoryOpen && (
        <VersionHistory
          apiPath={`api/student/practice-groups/${group.id}/practicals/${selectedStatObj?.practicalNumber}/versions`}
          apiQueryParams={{ kind: selectedStatObj?.kind }}
          onCancel={() => setIsVersionHistoryOpen(false)}
        />
      )}
      {pendingFileDeletion && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-head">
              <h2>Подтверждение удаления</h2>
            </div>
            <div className="modal-body confirmation-text">
              {pendingFileDeletion.deleteWarning}
            </div>
            <div className="modal-foot">
              <button
                className="btn ghost"
                onClick={() => setPendingFileDeletion(null)}
              >
                Отмена
              </button>
              <button className="btn primary" onClick={confirmFileDeletion}>
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
